import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-socialmedia.js";
import { C_ADDITIONAL, C_BTWNR, C_CITY, C_COMPANY, C_EMAIL, C_FIRSTNAME, C_LASTNAME, C_PHONE1, C_PROPERTY_DATE, C_STREET, C_WEBSITE } from "../types.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { msg, str } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import "@progressive-development/pd-forms/pd-form-row";
import "@progressive-development/pd-forms/pd-input";
import "@progressive-development/pd-forms/pd-checkbox";
import "@progressive-development/pd-forms/pd-form-container";
import "@progressive-development/pd-forms/pd-select";
import "@progressive-development/pd-forms/pd-radio-group";
//#region src/pd-contact/PdContact.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var countryPrefixes = {
	de: "+49",
	be: "+32",
	nl: "+31",
	fr: "+33",
	es: "+34"
};
function transformPhone(phone, country) {
	if (!phone || typeof phone !== "string") return "";
	const countryPrefix = countryPrefixes[country];
	if (!countryPrefix) throw new Error(`Unbekanntes Land: ${country}`);
	const cleanedPhone = phone.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
	if (cleanedPhone.startsWith("+")) return cleanedPhone;
	if (cleanedPhone.startsWith("0")) return countryPrefix + cleanedPhone.slice(1);
	return "";
}
var yearSelection = [{
	value: "UNDEF",
	name: msg("Baujahr auswählen", { id: "pd.contact.property.year.selection" })
}, ...Array.from({ length: 12 }, (_, i) => {
	const year = (2014 + i).toString();
	return {
		value: year,
		name: year
	};
})];
/**
* Contact component for displaying and editing contact information.
*
* @tagname pd-contact
* @summary Contact form/view with private/business support and validation.
*
* @csspart card-wrapper - Outer container for the card view layout, provides container query context.
*
* @cssprop --pd-contact-address-col - Text color for address content. Default: `var(--pd-default-font-content-col)`.
* @cssprop --pd-contact-address-title-col - Text color for address title. Default: `var(--pd-default-font-title-col)`.
* @cssprop --pd-contact-icon-col - Icon fill color. Default: `var(--pd-default-col)`.
* @cssprop --pd-contact-card-bg - Card view background color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-contact-card-divider-col - Card divider line color. Default: `var(--pd-default-disabled-light-col)`.
* @cssprop --pd-contact-card-padding - Card inner padding. Default: `2rem`.
* @cssprop --pd-contact-card-radius - Card border radius. Default: `var(--pd-radius-lg)`.
* @cssprop --pd-contact-card-shadow - Card box shadow. Default: `var(--pd-shadow-xl)`.
* @cssprop --pd-contact-card-subtitle-col - Card subtitle (real name) text color. Default: `var(--pd-default-font-muted-col)`.
* @cssprop --pd-contact-card-title-col - Card title (display name) text color. Default: `var(--pd-default-font-title-col)`.
*/
var PdContact = class PdContact extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.addressTitle = msg("Adresse", { id: "pd.contact.address.title" });
		this.phoneMailLink = false;
		this.summary = false;
		this.viewType = "detail";
		this.cardLayout = "auto";
		this.withPropertyDate = false;
		this.requiredFields = [];
		this.inputFields = [];
		this.requiredFieldInfo = true;
		this._business = false;
		this._socialMediaItems = [];
	}
	static {
		this.styles = [css`
      :host {
        display: block;
      }

      .contact {
        display: flex;
        flex-direction: column;
      }

      address {
        color: var(
          --pd-contact-address-col,
          var(--pd-default-font-content-col)
        );
        line-height: 1.8;
        font-style: normal;
      }

      dl {
        margin: 0;
        padding: 0;
      }

      dt {
        font-weight: bold;
        padding-top: 10px;
        color: var(
          --pd-contact-address-title-col,
          var(--pd-default-font-title-col)
        );
      }

      dd {
        font-weight: 400;
        font-size: 1em;
        margin: 0;
        padding: 0;
      }

      dd.larger {
        padding-top: 3px;
        padding-bottom: 5px;
      }

      .contact-form {
        --row-padding-top: 10px;
      }

      .link-icon {
        --pd-icon-bg-col-active: #859900;
        --pd-icon-bg-col-hover: #859900;
        --pd-icon-size: 14px;
        --pd-icon-col-active: white;
      }

      .link-item {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: var(--pd-default-font-link-col, inherit);
      }

      .link-item:hover {
        color: var(--pd-default-font-link-col-hover, #451a46);
      }

      /* ========================================
         DETAIL VIEW STYLES
         ======================================== */

      .detail-view {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-md, 1rem);
      }

      .detail-names {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-xs, 0.25rem);
      }

      .detail-title {
        margin: 0;
        font-family: var(--pd-default-font-title-family, inherit);
        font-size: 1.25rem;
        font-weight: 700;
        color: var(--pd-default-font-title-col, #0a3a48);
        line-height: 1.3;
      }

      .detail-company,
      .detail-vat {
        margin: 0;
        color: var(--pd-default-font-content-col, #353738);
      }

      .detail-name {
        margin: 0;
        color: var(--pd-default-font-muted-col, #6b7280);
        font-weight: 300;
      }

      .detail-address {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-xs, 0.25rem);
      }

      .detail-address p {
        margin: 0;
        color: var(--pd-default-font-content-col, #353738);
        line-height: 1.5;
      }

      .detail-contact-info {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-sm, 0.5rem);
      }

      .detail-bank {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-xs, 0.25rem);
      }

      .detail-bank p {
        margin: 0;
        color: var(--pd-default-font-content-col, #353738);
      }

      .detail-label {
        font-weight: 700;
        color: var(--pd-default-font-title-col, #0a3a48) !important;
        padding-top: var(--pd-spacing-sm, 0.5rem);
      }

      .detail-divider {
        border: none;
        border-top: 1px solid var(--pd-default-disabled-light-col, #e0e0e0);
        margin: var(--pd-spacing-sm, 0.5rem) 0;
        opacity: 0.5;
      }

      /* ========================================
         CARD VIEW STYLES
         ======================================== */

      .card-wrapper {
        container-type: inline-size;
        container-name: contact-card;
      }

      .card {
        display: grid;
        background: var(
          --pd-contact-card-bg,
          var(--pd-default-bg-col, #fefefe)
        );
        border-radius: var(--pd-contact-card-radius, var(--pd-radius-lg, 8px));
        box-shadow: var(
          --pd-contact-card-shadow,
          var(--pd-shadow-xl, 0 8px 16px rgba(0, 0, 0, 0.15))
        );
        padding: var(--pd-contact-card-padding, 2rem);
        overflow: hidden;
      }

      .card-content {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-md, 1rem);
      }

      .card-logo {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .card-logo img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      .card-names {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-xs, 0.25rem);
      }

      .title-name {
        margin: 0;
        font-family: var(--pd-default-font-title-family, inherit);
        color: var(
          --pd-contact-card-title-col,
          var(--pd-default-font-title-col, #0a3a48)
        );
        font-weight: 700;
        line-height: 1.2;
      }

      .real-name {
        margin: 0;
        font-family: var(--pd-default-font-content-family, inherit);
        color: var(
          --pd-contact-card-subtitle-col,
          var(--pd-default-font-muted-col, #6b7280)
        );
        font-weight: 300;
      }

      .card-contact-info {
        display: flex;
        flex-direction: column;
        gap: var(--pd-spacing-sm, 0.5rem);
      }

      .contact-item {
        display: flex;
        align-items: center;
        gap: var(--pd-spacing-sm, 0.5rem);
        text-decoration: none;
        color: var(--pd-default-font-content-col, #353738);
        font-size: var(--pd-default-font-content-size, 1rem);
      }

      .contact-item:hover {
        color: var(--pd-default-font-link-col-hover, #ffc857);
      }

      .contact-icon {
        --pd-icon-size: 16px;
        --pd-icon-col: var(
          --pd-contact-icon-col,
          var(--pd-default-col, #067394)
        );
        flex-shrink: 0;
      }

      .card-divider {
        border: none;
        border-top: 1px solid
          var(
            --pd-contact-card-divider-col,
            var(--pd-default-disabled-light-col, #e0e0e0)
          );
        margin: var(--pd-spacing-sm, 0.5rem) 0;
        opacity: 0.5;
      }

      /* VERTICAL LAYOUT (default for narrow containers) */
      @container contact-card (max-width: 499px) {
        .card:not(.layout-horizontal) {
          grid-template-rows: auto 1fr;
          grid-template-columns: 1fr;
          text-align: center;
        }

        .card:not(.layout-horizontal) .card-logo {
          width: 100%;
          max-height: 150px;
          padding-bottom: var(--pd-spacing-md, 1rem);
        }

        .card:not(.layout-horizontal) .card-names {
          align-items: center;
        }

        .card:not(.layout-horizontal) .title-name {
          font-size: 1.25rem;
        }

        .card:not(.layout-horizontal) .real-name {
          font-size: 0.9rem;
        }

        .card:not(.layout-horizontal) .card-contact-info {
          align-items: center;
        }

        .card:not(.layout-horizontal) .contact-item {
          justify-content: center;
          font-size: 0.85rem;
        }

        .card:not(.layout-horizontal) pd-socialmedia {
          display: flex;
          justify-content: center;
        }
      }

      /* HORIZONTAL LAYOUT (for wider containers) */
      @container contact-card (min-width: 500px) {
        .card:not(.layout-vertical) {
          grid-template-columns: 1fr minmax(120px, 200px);
          grid-template-rows: 1fr;
          align-items: start;
          gap: var(--pd-spacing-md, 1rem);
        }

        .card:not(.layout-vertical) .card-logo {
          order: 2;
          width: 100%;
        }

        .card:not(.layout-vertical) .card-content {
          order: 1;
        }

        .card:not(.layout-vertical) .title-name {
          font-size: 1.5rem;
        }

        .card:not(.layout-vertical) .real-name {
          font-size: 1rem;
        }

        .card:not(.layout-vertical) .contact-item {
          font-size: 0.9rem;
        }
      }

      /* FORCED HORIZONTAL LAYOUT */
      .card.layout-horizontal {
        grid-template-columns: 1fr minmax(120px, 200px) !important;
        grid-template-rows: 1fr !important;
        text-align: left !important;
        align-items: start !important;
        gap: var(--pd-spacing-md, 1rem) !important;
      }

      .card.layout-horizontal .card-logo {
        order: 2 !important;
        width: 100% !important;
      }

      .card.layout-horizontal .card-content {
        order: 1 !important;
      }

      .card.layout-horizontal .title-name {
        font-size: 1.5rem;
      }

      .card.layout-horizontal .real-name {
        font-size: 1rem;
      }

      /* FORCED VERTICAL LAYOUT */
      .card.layout-vertical {
        grid-template-rows: auto 1fr !important;
        grid-template-columns: 1fr !important;
        text-align: center !important;
      }

      .card.layout-vertical .card-logo {
        width: 100% !important;
        max-height: 150px !important;
        padding-bottom: var(--pd-spacing-md, 1rem);
      }

      .card.layout-vertical .card-names {
        align-items: center !important;
      }

      .card.layout-vertical .card-contact-info {
        align-items: center !important;
      }

      .card.layout-vertical .contact-item {
        justify-content: center;
      }

      .card.layout-vertical pd-socialmedia {
        display: flex;
        justify-content: center;
      }

      .card.layout-vertical .title-name {
        font-size: 1.25rem;
      }

      .card.layout-vertical .real-name {
        font-size: 0.9rem;
      }
    `];
	}
	willUpdate(changedProps) {
		if (changedProps.has("contact") && this.contact) {
			this._business = this.contact.business ?? false;
			this._socialMediaItems = this.contact.socialMedia ?? [];
		}
	}
	render() {
		return html`
      <div class="contact">
        ${this.viewType === "card" && this.summary ? this._renderCardView() : this.summary ? this._renderViewContact() : this._renderEditContact()}
      </div>
    `;
	}
	_renderEditContact() {
		return html`
      <pd-form-container
        id="contactContainerId"
        ?requiredFieldInfo="${this.requiredFieldInfo}"
        autoTrimm
      >
        ${this.inputFields.length === 0 || this._showInput("type") ? html`
                <pd-form-row>
                  <pd-radio-group
                    id="contactTypeRadioId"
                    label="${msg("Typ", { id: "pd.contact.label.type" })}"
                    required
                    initValue="${this._getRadioValue()}"
                    @pd-form-element-change="${this._switchAddressType}"
                    style="--group-gap: 20px;"
                  >
                    <pd-checkbox
                      checkType="RADIO"
                      initValue="${this.contact ? !this.contact.business : true}"
                      valueName="private"
                      >${msg("Privatperson", { id: "pd.contact.check.private" })}</pd-checkbox
                    >
                    <pd-checkbox
                      checkType="RADIO"
                      valueName="business"
                      initValue="${this.contact ? this.contact.business || false : false}"
                      >${msg("Unternehmen", { id: "pd.contact.check.company" })}</pd-checkbox
                    >
                  </pd-radio-group>
                </pd-form-row>
              ` : ""}
        ${this._business ? html`
                ${this.inputFields.length === 0 || this._showInput("companyName") ? html`
                        <pd-form-row class="contact-form">
                          <pd-input
                            id="compNameId"
                            label="${msg("Name des Unternehmen", { id: "pd.contact.label.company" })}"
                            ?required="${this._isRequired(C_COMPANY)}"
                            initValue="${this.contact?.companyName || ""}"
                            valueName="companyName"
                            autoCompleteName="organization"
                          ></pd-input>
                        </pd-form-row>
                      ` : ""}
                ${this.inputFields.length === 0 || this._showInput("vatNr") ? html`
                        <pd-form-row class="contact-form">
                          <pd-input
                            id="vatId"
                            label="${msg("USt-IdNr.", { id: "pd.contact.label.btw" })}"
                            ?required="${this._isRequired(C_BTWNR)}"
                            fieldType="vat"
                            valueName="vatNr"
                            initValue="${this.contact?.vatNr || ""}"
                            autoCompleteName="vat"
                          ></pd-input>
                        </pd-form-row>
                      ` : ""}
              ` : html`
                ${this.inputFields.length === 0 || this._showInput("firstName") ? html`
                        <pd-form-row class="contact-form">
                          <pd-input
                            id="firstNameId"
                            label="${msg("Vorname", { id: "pd.contact.label.firstName" })}"
                            valueName="firstName"
                            initValue="${this.contact?.firstName || ""}"
                            autoCompleteName="given-name"
                            ?required="${this._isRequired(C_FIRSTNAME)}"
                          ></pd-input>
                        </pd-form-row>
                      ` : ""}
                ${this.inputFields.length === 0 || this._showInput("lastName") ? html`
                        <pd-form-row class="contact-form">
                          <pd-input
                            id="lastNameId"
                            label="${msg("Nachname", { id: "pd.contact.label.lastName" })}"
                            valueName="lastName"
                            initValue="${this.contact?.lastName || ""}"
                            autoCompleteName="family-name"
                            ?required="${this._isRequired(C_LASTNAME)}"
                          ></pd-input>
                        </pd-form-row>
                      ` : ""}
              `}
        ${this.inputFields.length === 0 || this._showInput("street") ? html`
                <pd-form-row class="contact-form">
                  <pd-input
                    id="streetId"
                    span="three-quarters"
                    label="${msg("Strasse", { id: "pd.contact.label.street" })}"
                    valueName="street"
                    initValue="${this.contact?.street || ""}"
                    autoCompleteName="street-address"
                    ?required="${this._isRequired(C_STREET)}"
                  ></pd-input>
                  <pd-input
                    id="streetNrId"
                    span="quarter"
                    label="${msg("Nr.", { id: "pd.contact.label.streetNr" })}"
                    valueName="streetNr"
                    initValue="${this.contact?.streetNr || ""}"
                    ?required="${this._isRequired(C_STREET)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this.inputFields.length === 0 || this._showInput("city") ? html`
                <pd-form-row class="contact-form">
                  ${this.match && this.match.zip ? html`
                          <pd-input
                            readonly
                            id="zipId"
                            span="third"
                            label="${msg("PLZ", { id: "pd.contact.label.zip" })}"
                            valueName="zip"
                            initValue="${this.match.zip}"
                          ></pd-input>
                        ` : html`
                          <pd-input
                            id="zipId"
                            span="third"
                            label="${msg("PLZ", { id: "pd.contact.label.zip" })}"
                            fieldType="number"
                            valueName="zip"
                            initValue="${this.contact?.zip || ""}"
                            autoCompleteName="postal-code"
                            ?required="${this._isRequired("zip")}"
                          ></pd-input>
                        `}
                  <pd-input
                    id="cityId"
                    span="two-thirds"
                    label="${msg("Ort", { id: "pd.contact.label.city" })}"
                    valueName="city"
                    initValue="${this.contact?.city || ""}"
                    autoCompleteName="locality"
                    ?required="${this._isRequired(C_CITY)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this.inputFields.length === 0 || this._showInput("additionalHint") ? html`
                <pd-form-row class="contact-form">
                  <pd-input
                    id="additionalHintId"
                    label="${msg("Addresszusatz", { id: "pd.contact.label.additional" })}"
                    initValue="${this.contact?.additionalHint || ""}"
                    valueName="additionalHint"
                    ?required="${this._isRequired(C_ADDITIONAL)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this.withPropertyDate ? html`
                <pd-form-row class="contact-form">
                  <pd-select
                    id="propertyDateId"
                    label="${msg("Datum der Immobilie", { id: "pd.contact.label.propertyDate" })}"
                    initValue="${this.contact?.propertyDate || ""}"
                    .values="${yearSelection}"
                    ?required="${this._isRequired(C_PROPERTY_DATE)}"
                  ></pd-select>
                </pd-form-row>
              ` : ""}
        ${this.inputFields.length === 0 || this._showInput("phone1") ? html`
                <pd-form-row class="contact-form">
                  <pd-input
                    id="phoneId"
                    label="${msg("Telefon", { id: "pd.contact.label.phone" })}"
                    name="phone"
                    valueName="phone1"
                    initValue="${this.contact?.phone1 || ""}"
                    fieldType="phone"
                    autoCompleteName="tel"
                    ?required="${this._isRequired(C_PHONE1)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this.inputFields.length === 0 || this._showInput("email") ? html`
                <pd-form-row class="contact-form">
                  <pd-input
                    id="mailId"
                    label="${msg("E-mail", { id: "pd.contact.label.email" })}"
                    fieldType="mail"
                    valueName="email"
                    initValue="${this.contact?.email || ""}"
                    autoCompleteName="email"
                    ?required="${this._isRequired(C_EMAIL)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this._showInput("website") ? html`
                <pd-form-row class="contact-form">
                  <pd-input
                    id="websiteId"
                    label="${msg("Website", { id: "pd.contact.label.website" })}"
                    fieldType="url"
                    valueName="website"
                    initValue="${this.contact?.website || ""}"
                    autoCompleteName="url"
                    ?required="${this._isRequired(C_WEBSITE)}"
                  ></pd-input>
                </pd-form-row>
              ` : ""}
        ${this._showInput("socialMedia") ? html`
                <pd-socialmedia
                  .initItems="${this._socialMediaItems}"
                  @items-change="${this._onSocialMediaChange}"
                ></pd-socialmedia>
              ` : ""}
      </pd-form-container>
    `;
	}
	_getRadioValue() {
		if (this.contact) return this.contact.business ? "business" : "private";
		return "private";
	}
	_renderViewContact() {
		if (!this.contact) return html`<p>Contact undefined</p>`;
		const trPhoneNr = transformPhone(this.contact.phone1 || "", this.contact.country || "de");
		const displayTitle = this.contact.titleName || this.addressTitle;
		const hasAddress = this._getFullStreet() || this._getFullLocation() || this.contact.additionalHint || this.contact.country || this.contact.propertyDate;
		const hasContactInfo = this.contact.phone1 || this.contact.email || this.contact.website;
		return html`
      <div class="detail-view">
        <div class="detail-names">
          ${displayTitle ? html`<h3 class="detail-title">${displayTitle}</h3>` : nothing}
          ${this.contact.companyName ? html`<p class="detail-company">${this.contact.companyName}</p>` : nothing}
          ${this.contact.vatNr ? html`<p class="detail-vat">${this.contact.vatNr}</p>` : nothing}
          ${this._getFullName() && this._getFullName() !== displayTitle ? html`<p class="detail-name">${this._getFullName()}</p>` : nothing}
        </div>

        ${hasAddress ? html`<div class="detail-address">
                ${this._getFullStreet() ? html`<p>${this._getFullStreet()}</p>` : nothing}
                ${this._getFullLocation() ? html`<p>${this._getFullLocation()}</p>` : nothing}
                ${this.contact.additionalHint ? html`<p>${this.contact.additionalHint}</p>` : nothing}
                ${this.contact.country ? html`<p>${this.contact.country}</p>` : nothing}
                ${this.contact.propertyDate ? html`<p>
                        ${msg(str`Baujahr: ${this.contact.propertyDate}`, { id: "pd.contact.label.summary.propertyDate" })}
                      </p>` : nothing}
              </div>` : nothing}
        ${hasContactInfo ? html`<div class="detail-contact-info">
                ${this.contact.phone1 ? html`
                        <a
                          href="tel:${trPhoneNr || this.contact.phone1}"
                          class="contact-item"
                          aria-label="Phone: ${this.contact.phone1}"
                        >
                          <pd-icon
                            icon="phoneIcon"
                            class="contact-icon"
                          ></pd-icon>
                          <span>${this.contact.phone1}</span>
                        </a>
                      ` : nothing}
                ${this.contact.email ? html`
                        <a
                          href="mailto:${this.contact.email}"
                          class="contact-item"
                          aria-label="Email: ${this.contact.email}"
                        >
                          <pd-icon
                            icon="mailIcon"
                            class="contact-icon"
                          ></pd-icon>
                          <span>${this.contact.email}</span>
                        </a>
                      ` : nothing}
                ${this.contact.website ? html`
                        <a
                          href="${this._ensureHttps(this.contact.website)}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="contact-item"
                          aria-label="Website: ${this.contact.website}"
                        >
                          <pd-icon
                            icon="circleIcon"
                            class="contact-icon"
                          ></pd-icon>
                          <span
                            >${this._formatWebsiteDisplay(this.contact.website)}</span
                          >
                        </a>
                      ` : nothing}
              </div>` : nothing}
        ${this.contact.btw || this.contact.kbc ? html`
                <div class="detail-bank">
                  ${this.contact.btw ? html`
                          <p class="detail-label">BTW</p>
                          <p>${this.contact.btw}</p>
                        ` : nothing}
                  ${this.contact.kbc ? html`
                          <p class="detail-label">Bankgegevens</p>
                          <p>${this.contact.kbc}</p>
                          ${this.contact.bank ? html`<p>${this.contact.bank}</p>` : nothing}
                        ` : nothing}
                </div>
              ` : nothing}
        ${this.contact.socialMedia && this.contact.socialMedia.length > 0 ? html`
                <hr class="detail-divider" />
                <pd-socialmedia
                  summary
                  .initItems="${this.contact.socialMedia}"
                ></pd-socialmedia>
              ` : nothing}
      </div>
    `;
	}
	_renderCardView() {
		if (!this.contact) return html`<p>Contact undefined</p>`;
		const layoutClass = this.cardLayout !== "auto" ? `layout-${this.cardLayout}` : "";
		return html`
      <div class="card-wrapper" part="card-wrapper">
        <div class="card ${layoutClass}">
          ${this._renderCardLogo()}
          <div class="card-content">
            ${this._renderCardNames()} ${this._renderCardContactInfo()}
            ${this._hasCardContactInfo() ? this._renderCardDivider() : nothing}
            ${this._renderCardSocialMedia()}
          </div>
        </div>
      </div>
    `;
	}
	_renderCardLogo() {
		if (!this.contact?.logo) return nothing;
		return html`
      <div class="card-logo">
        <img
          src="${this.contact.logo}"
          alt="${this.contact.titleName || "Logo"}"
        />
      </div>
    `;
	}
	_renderCardNames() {
		const titleName = this.contact?.titleName;
		const fullName = this._getFullName();
		return html`
      <div class="card-names">
        ${titleName ? html`<h2 class="title-name">${titleName}</h2>` : nothing}
        ${fullName ? html`<p class="real-name">${fullName}</p>` : nothing}
      </div>
    `;
	}
	_renderCardContactInfo() {
		const phone = this.contact?.phone1;
		const email = this.contact?.email;
		const website = this.contact?.website;
		const trPhoneNr = phone ? transformPhone(phone, this.contact?.country || "de") : "";
		return html`
      <div class="card-contact-info">
        ${phone ? html`
                <a
                  href="tel:${trPhoneNr || phone}"
                  class="contact-item"
                  aria-label="Phone: ${phone}"
                >
                  <pd-icon icon="phoneIcon" class="contact-icon"></pd-icon>
                  <span>${phone}</span>
                </a>
              ` : nothing}
        ${email ? html`
                <a
                  href="mailto:${email}"
                  class="contact-item"
                  aria-label="Email: ${email}"
                >
                  <pd-icon icon="mailIcon" class="contact-icon"></pd-icon>
                  <span>${email}</span>
                </a>
              ` : nothing}
        ${website ? html`
                <a
                  href="${this._ensureHttps(website)}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="contact-item"
                  aria-label="Website: ${website}"
                >
                  <pd-icon icon="circleIcon" class="contact-icon"></pd-icon>
                  <span>${this._formatWebsiteDisplay(website)}</span>
                </a>
              ` : nothing}
      </div>
    `;
	}
	_renderCardDivider() {
		return html`<hr class="card-divider" />`;
	}
	_renderCardSocialMedia() {
		const socialItems = this.contact?.socialMedia;
		if (!socialItems || socialItems.length === 0) return nothing;
		return html`
      <pd-socialmedia summary .initItems="${socialItems}"></pd-socialmedia>
    `;
	}
	_hasCardContactInfo() {
		return !!(this.contact?.phone1 || this.contact?.email || this.contact?.website);
	}
	_ensureHttps(url) {
		if (!url) return "";
		return /^https?:\/\//i.test(url) ? url : `https://${url}`;
	}
	_formatWebsiteDisplay(url) {
		return url.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");
	}
	get valid() {
		return this._contactForm?.valid === true;
	}
	async triggerValidate() {
		return this._contactForm?.triggerValidate();
	}
	getValues() {
		const contactFormValues = this._contactForm?.getValues().origin;
		const socialItems = this._socialMediaEl?.value ?? this._socialMediaItems;
		const commonValues = {
			business: this._business,
			street: contactFormValues.street,
			streetNr: contactFormValues.streetNr,
			zip: contactFormValues.zip,
			city: contactFormValues.city,
			additionalHint: contactFormValues.additionalHint,
			propertyDate: contactFormValues.propertyDate,
			phone1: contactFormValues.phone1,
			email: contactFormValues.email,
			...contactFormValues.website ? { website: contactFormValues.website } : {},
			...socialItems.length ? { socialMedia: socialItems } : {}
		};
		return this._business ? {
			...commonValues,
			companyName: contactFormValues.companyName,
			vatNr: contactFormValues.vatNr
		} : {
			...commonValues,
			firstName: contactFormValues.firstName,
			lastName: contactFormValues.lastName
		};
	}
	_switchAddressType(e) {
		if (e.detail.name === "contactTypeRadioId") this._business = e.detail.value === "business";
		e.stopPropagation();
	}
	_onSocialMediaChange(e) {
		this._socialMediaItems = e.detail?.items ?? [];
		e.stopPropagation();
	}
	_isRequired(field) {
		return this.requiredFields.length > 0 ? this.requiredFields.includes(field) : false;
	}
	_showInput(field) {
		return this.inputFields.length > 0 ? this.inputFields.includes(field) : true;
	}
	_setFormData() {
		const getInput = (id) => (this.shadowRoot?.getElementById(id))?.value;
		return {
			business: this._business,
			companyName: this._business ? getInput("compNameId") : void 0,
			vatNr: this._business ? getInput("vatId") : void 0,
			firstName: !this._business ? getInput("firstNameId") : void 0,
			lastName: !this._business ? getInput("lastNameId") : void 0,
			street: getInput("streetId"),
			streetNr: getInput("streetNrId"),
			additionalHint: getInput("additionalHintId"),
			propertyDate: this.withPropertyDate ? getInput("propertyDateId") : void 0,
			zip: getInput("zipId"),
			city: getInput("cityId"),
			phone1: getInput("phoneId"),
			email: getInput("mailId")
		};
	}
	_getFullName() {
		return PdContact._getFullVal(this.contact?.firstName, this.contact?.lastName);
	}
	_getFullStreet() {
		return PdContact._getFullVal(this.contact?.street, this.contact?.streetNr);
	}
	_getFullLocation() {
		return PdContact._getFullVal(this.contact?.zip, this.contact?.city);
	}
	static _getFullVal(val1, val2, fallback = "") {
		return [val1, val2].filter(Boolean).join(" ") || fallback;
	}
};
__decorate([property()], PdContact.prototype, "addressTitle", void 0);
__decorate([property({ type: Boolean })], PdContact.prototype, "phoneMailLink", void 0);
__decorate([property({ type: Boolean })], PdContact.prototype, "summary", void 0);
__decorate([property({
	type: String,
	reflect: true,
	attribute: "view-type"
})], PdContact.prototype, "viewType", void 0);
__decorate([property({
	type: String,
	reflect: true,
	attribute: "card-layout"
})], PdContact.prototype, "cardLayout", void 0);
__decorate([property({ type: Boolean })], PdContact.prototype, "withPropertyDate", void 0);
__decorate([property({ type: Array })], PdContact.prototype, "requiredFields", void 0);
__decorate([property({ type: Array })], PdContact.prototype, "inputFields", void 0);
__decorate([property({ type: Boolean })], PdContact.prototype, "requiredFieldInfo", void 0);
__decorate([property({ type: Object })], PdContact.prototype, "contact", void 0);
__decorate([property({ type: Object })], PdContact.prototype, "match", void 0);
__decorate([state()], PdContact.prototype, "_business", void 0);
__decorate([state()], PdContact.prototype, "_socialMediaItems", void 0);
__decorate([query("#contactContainerId")], PdContact.prototype, "_contactForm", void 0);
__decorate([query("pd-socialmedia")], PdContact.prototype, "_socialMediaEl", void 0);
//#endregion
export { PdContact };
